// ============================================================================
// HOME - V11.0 - REESTRUTURAÇÃO EDITORIAL
// ============================================================================

import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  Globe,
  Shield,
  Users,
  CheckCircle,
  ArrowRight,
  Quote,
  Instagram,
  ShoppingBag,
  MapPin,
  GraduationCap,
  Building2,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  School,
  Star,
} from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { API_BASE } from '../utils/apiConfig';
import EscolasGrid from './EscolasGrid';
// ─── AnimatedSection ─────────────────────────────────────────────────────────

const AnimatedSection: React.FC<{
  children: React.ReactNode;
  animation?: 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right';
  delay?: number;
}> = ({ children, animation = 'fade-up', delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation();

  const cls = (() => {
    const base = 'transition-all duration-1000 ease-out';
    if (!isVisible) {
      switch (animation) {
        case 'fade-up':    return `${base} opacity-0 translate-y-10`;
        case 'fade-in':    return `${base} opacity-0`;
        case 'slide-left': return `${base} opacity-0 -translate-x-10`;
        case 'slide-right':return `${base} opacity-0 translate-x-10`;
        default:           return `${base} opacity-0`;
      }
    }
    return `${base} opacity-100 translate-y-0 translate-x-0`;
  })();

  return (
    <div ref={ref} className={cls} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

// ─── BookGallery ──────────────────────────────────────────────────────────────
// 8 formandos photos with a 3-D book-flip effect
const BOOK_PHOTOS = [
  '/f1.jpeg',
  '/f2.jpeg',
  '/f3.jpeg',
  '/f4.jpeg',
  '/f5.jpeg',
  '/f6.jpeg',
  '/f7.jpeg',
  '/f8.jpeg',
];

const BookGallery: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState<'next' | 'prev'>('next');

  const openBook = () => setIsOpen(true);

  const goNext = () => {
    if (currentPage < BOOK_PHOTOS.length - 1 && !isFlipping) {
      setFlipDirection('next');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage((p) => p + 1);
        setIsFlipping(false);
      }, 400);
    }
  };

  const goPrev = () => {
    if (currentPage > 0 && !isFlipping) {
      setFlipDirection('prev');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage((p) => p - 1);
        setIsFlipping(false);
      }, 400);
    }
  };

  return (
    <div className="relative flex items-center justify-center select-none">
      <style>{`
        @keyframes flipNext {
          0%   { transform: perspective(1200px) rotateY(0deg);   }
          50%  { transform: perspective(1200px) rotateY(-90deg); opacity:.5; }
          100% { transform: perspective(1200px) rotateY(0deg);   }
        }
        @keyframes flipPrev {
          0%   { transform: perspective(1200px) rotateY(0deg);  }
          50%  { transform: perspective(1200px) rotateY(90deg); opacity:.5; }
          100% { transform: perspective(1200px) rotateY(0deg);  }
        }
        .flip-next { animation: flipNext .4s ease-in-out; }
        .flip-prev { animation: flipPrev .4s ease-in-out; }

        /* Desktop hover: cover scales + shadow deepens */
        .book-cover-wrap:hover .book-cover-img { transform: scale(1.03) rotateY(-5deg); }
        .book-cover-img {
          transition: transform .5s ease, box-shadow .5s ease;
          transform-style: preserve-3d;
        }
      `}</style>

      {!isOpen ? (
        /* ── CAPA DO LIVRO ── */
        <div
          className="book-cover-wrap cursor-pointer"
          onClick={openBook}
          title="Clique para abrir o álbum"
        >
          <div className="book-cover-img relative w-[260px] sm:w-[320px] md:w-[380px] aspect-[3/4] rounded-2xl overflow-hidden shadow-[8px_12px_40px_rgba(0,0,0,.35)] border-4 border-amber-200">
            {/* spine */}
            <div className="absolute left-0 top-0 h-full w-5 bg-gradient-to-r from-amber-700 to-amber-500 z-10 rounded-l-xl" />
            {/* cover background */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-800 via-amber-600 to-orange-500" />
            {/* subtle texture */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)",
                backgroundSize: '8px 8px',
              }}
            />
            {/* logo */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-6 z-20">
              <div className="w-28 h-28 sm:w-36 sm:h-36 bg-white/15 backdrop-blur-sm rounded-full p-3 shadow-lg flex items-center justify-center border border-white/30">
                <img
                  src="/imagem/Bras.png"
                  alt="Logo Federação"
                  className="w-full h-full object-contain drop-shadow-lg"
                />
              </div>
              <div className="text-center">
                <p className="text-white font-black text-lg tracking-wide drop-shadow">Álbum de Formandos</p>
                <p className="text-amber-100 text-sm mt-1 font-light">Federação Internacional</p>
              </div>
              {/* CTA */}
              <div className="mt-2 px-5 py-2 bg-white/20 backdrop-blur border border-white/40 rounded-full flex items-center gap-2 shadow">
                <BookOpen className="w-4 h-4 text-amber-100" />
                <span className="text-white text-sm font-semibold">Role ou clique para ver</span>
              </div>
            </div>
            {/* page edges on right */}
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute right-0 top-0 h-full bg-amber-50 rounded-r-2xl"
                style={{
                  width: `${4 + i * 2}px`,
                  right: `${-(i + 1) * 3}px`,
                  opacity: 0.6 - i * 0.08,
                  zIndex: -i,
                }}
              />
            ))}
          </div>
          {/* scroll hint for desktop */}
          <p className="text-center text-sm text-amber-600 mt-4 font-medium animate-pulse md:block hidden">
            ↕ Role ou clique para abrir
          </p>
        </div>
      ) : (
        /* ── LIVRO ABERTO ── */
        <div className="flex flex-col items-center gap-6 w-full">
          {/* photo spread */}
          <div className="relative w-full max-w-sm md:max-w-md">
            {/* left page (previous photo thumbnail hint) */}
            <div
              className={`relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200 ${
                isFlipping
                  ? flipDirection === 'next'
                    ? 'flip-next'
                    : 'flip-prev'
                  : ''
              }`}
            >
              {/* spine on left */}
              <div className="absolute left-0 top-0 h-full w-4 bg-gradient-to-r from-amber-700/60 to-transparent z-10" />
              <img
                src={BOOK_PHOTOS[currentPage]}
                alt={`Formando ${currentPage + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              {/* page number */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/40 backdrop-blur-sm rounded-full text-white text-xs font-bold">
                {currentPage + 1} / {BOOK_PHOTOS.length}
              </div>
            </div>
          </div>

          {/* navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={goPrev}
              disabled={currentPage === 0}
              className="w-11 h-11 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center disabled:opacity-30 hover:bg-amber-200 transition-colors shadow"
            >
              <ChevronLeft className="w-5 h-5 text-amber-800" />
            </button>

            {/* dot indicators */}
            <div className="flex gap-1.5">
              {BOOK_PHOTOS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === currentPage
                      ? 'bg-amber-600 w-5'
                      : 'bg-amber-200 hover:bg-amber-400'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={goNext}
              disabled={currentPage === BOOK_PHOTOS.length - 1}
              className="w-11 h-11 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center disabled:opacity-30 hover:bg-amber-200 transition-colors shadow"
            >
              <ChevronRight className="w-5 h-5 text-amber-800" />
            </button>
          </div>

          {/* close / back to cover */}
          <button
            onClick={() => { setIsOpen(false); setCurrentPage(0); }}
            className="text-sm text-amber-600 underline underline-offset-2 hover:text-amber-800 transition-colors"
          >
            ← Fechar álbum
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Home ─────────────────────────────────────────────────────────────────────
const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">

      {/* ══════════════════════════════════════════════════════════════
          1. HERO — texto enxuto, sem os dois parágrafos complementares
      ══════════════════════════════════════════════════════════════ */}
<section className="relative min-h-[80vh] md:min-h-screen flex items-center bg-gradient-to-br from-amber-50 via-amber-100/40 to-white text-gray-900 overflow-hidden pt-24 md:pt-28">
        {/* Decorative planet */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[350px] h-[350px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] pointer-events-none z-0 md:z-10 opacity-60">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-200/40 via-amber-300/25 to-orange-200/30 blur-2xl animate-pulse" style={{ animationDuration: '5s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] h-[35%] rounded-full border border-amber-300/25 rotate-[-20deg] blur-sm opacity-50" />
          <div className="absolute top-1/3 right-1/4 w-40 h-40 md:w-56 md:h-56 bg-amber-100/30 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/4 w-2.5 h-2.5 bg-amber-400/50 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
          <div className="absolute bottom-1/3 left-1/4 w-2 h-2 bg-orange-300/40 rounded-full animate-pulse" style={{ animationDuration: '4s', animationDelay: '1s' }} />
        </div>

        <div className="absolute top-20 right-[15%] w-48 h-48 md:w-80 md:h-80 bg-amber-200/15 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-20 left-0 w-64 h-64 md:w-96 md:h-96 bg-orange-200/10 rounded-full blur-3xl opacity-40" />

        <div className="container max-w-7xl mx-auto px-6 md:px-12 relative z-20">
          <div className="grid md:grid-cols-[minmax(0,1fr)_10rem] lg:grid-cols-[minmax(0,1fr)_13rem] xl:grid-cols-[minmax(0,1fr)_15rem] items-center gap-8 lg:gap-12">
          <div className="max-w-3xl">

            <AnimatedSection animation="fade-up" delay={200}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6 tracking-tight text-gray-900">
                <span className="bg-gradient-to-r from-amber-700 via-amber-600 to-orange-600 bg-clip-text text-transparent">
                  Federação das Escolas Profissionais de Cabeleireiros
                  <br />
                  e Similares do Brasil Internacional
                </span>
              </h1>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={300}>
              <p className="text-lg md:text-xl text-gray-700 mb-10 max-w-2xl leading-relaxed font-light">
                A Federação é uma entidade de caráter institucional, educacional e representativo, com atuação consolidada há mais de{' '}
                <strong className="font-semibold text-amber-800">70 anos</strong> na regulamentação, licenciamento, valorização, reconhecimento profissional e na validação educacional de profissionais, educadores, gestores, escolas e academias do setor da beleza.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={500}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/filiacao"
                  className="group relative px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span>Filiar-se Agora</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/publicacoes/profissionais"
                  className="px-8 py-4 bg-white/90 backdrop-blur-sm text-amber-900 font-bold rounded-xl border-2 border-amber-300 hover:bg-amber-50 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 shadow-md"
                >
                  <span>Consulte seu Registro</span>
                  <Globe className="w-5 h-5" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={700}>
              <div className="mt-12 flex flex-wrap gap-6 items-center">
                {[
                  { icon: <Award className="w-5 h-5 text-amber-700" />, label: '70+ Anos de Atuação' },
                  { icon: <Globe className="w-5 h-5 text-amber-700" />, label: '+100 Países' },
                  { icon: <Shield className="w-5 h-5 text-amber-700" />, label: 'Reconhecimento Internacional' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-lg border border-amber-200/50">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">{b.icon}</div>
                    <span className="text-sm font-semibold text-gray-800">{b.label}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

          </div>

          {/* Logo integrada ao conteúdo para manter a proximidade em telas largas */}
          <div className="hidden md:flex items-center justify-center" style={{ animation: 'fadeInScale 0.8s ease-out 0.6s both' }}>
            <img src="/imagem/Bras.png" alt="Logo Federação" className="w-full max-w-40 lg:max-w-52 xl:max-w-60 h-auto opacity-90 hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
          </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeInScale { from{opacity:0;transform:scale(.8)} to{opacity:1;transform:scale(1)} }
        `}</style>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          1-B. INFO BAND — parágrafos removidos do hero, reposicionados
               com visual limpo de dois cards lado a lado
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-amber-700 via-amber-600 to-orange-600 text-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">

            <AnimatedSection animation="slide-right" delay={100}>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center mt-1">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <p className="text-amber-50 leading-relaxed text-[15px]">
                  Com presença nacional e internacional, a Federação estabelece critérios técnicos, normativos e pedagógicos, promovendo a qualificação profissional, a padronização de boas práticas e o fortalecimento institucional do segmento em{' '}
                  <strong className="text-white">mais de 100 países</strong>.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slide-left" delay={200}>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center mt-1">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <p className="text-amber-50 leading-relaxed text-[15px]">
                  A filiação institucional assegura <strong className="text-white">reconhecimento formal</strong>, formação válida no Brasil e no exterior, além de suporte técnico, educacional e institucional contínuo, garantindo segurança, credibilidade e respaldo em todas as etapas da trajetória profissional.
                </p>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          2. O NÚCLEO DA NOSSA MISSÃO — inalterado
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12">

          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                  <img src="/icons/icon10.png" alt="Nossa Essência" className="w-6 h-6 object-contain" />
                </div>
                <span className="text-sm font-bold text-amber-900 uppercase tracking-wider">Nossa Essência</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">
                O Núcleo da Nossa Missão
              </h2>
            </div>
          </AnimatedSection>

          <div className="max-w-5xl mx-auto">
            <AnimatedSection animation="fade-up" delay={200}>
              <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed text-center">
                A <span className="font-black text-amber-600">valorização, o reconhecimento e o desenvolvimento educacional</span> dos profissionais da beleza constituem o{' '}
                <span className="font-bold text-gray-900 underline decoration-amber-500 decoration-2 underline-offset-4">eixo central da atuação institucional</span> da Federação.
              </p>
            </AnimatedSection>
            <AnimatedSection animation="fade-up" delay={300}>
              <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed text-center">
                Mais do que <span className="font-bold text-gray-800">técnica e criatividade</span>, o setor da beleza é sustentado por{' '}
                <span className="font-black text-amber-600">formação qualificada, organização educacional e unidade institucional</span>. A Federação atua como{' '}
                <span className="font-bold text-gray-900 bg-amber-50 px-2 py-1 rounded">instância integradora</span>, conectando profissionais, educadores, escolas e instituições, promovendo a elevação dos{' '}
                <span className="font-bold text-gray-800">padrões técnicos, pedagógicos e éticos</span> que orientam a formação profissional.
              </p>
            </AnimatedSection>
            <AnimatedSection animation="fade-up" delay={400}>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed text-center">
                Por meio de <span className="font-bold text-gray-800">diretrizes educacionais, programas de formação e reconhecimento institucional</span>, a Federação contribui para o{' '}
                <span className="font-black text-amber-600">desenvolvimento técnico, ético e profissional</span> do setor da beleza em{' '}
                <span className="font-bold text-gray-900 border-b-2 border-amber-500">âmbito nacional e internacional</span>.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          3. NOVA SEÇÃO — "Como Funciona" (5 passos) acima de Benefícios
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-900 text-white relative overflow-hidden">

        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">

          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur rounded-full border border-white/20 mb-6">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                <span className="text-sm font-bold text-amber-200 uppercase tracking-wider">Simples e Rápido</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
                Filie-se em <span className="text-amber-400">5 passos</span>
              </h2>
              <p className="text-amber-100 text-lg max-w-2xl mx-auto">
                O processo de filiação é descomplicado. Veja como é fácil fazer parte da maior rede de profissionais da beleza do Brasil.
              </p>
            </div>
          </AnimatedSection>

          {/* Steps — horizontal on md+, vertical stack on mobile */}
          <div className="relative max-w-6xl mx-auto">

            {/* connecting line (desktop only) */}
            <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-amber-600/0 via-amber-500/60 to-amber-600/0 z-0" />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
              {[
                {
                  step: '01',
                  icon: '/icons/icone2.png',
                  title: 'Cadastro',
                  text: 'Preencha o formulário de filiação online em poucos minutos, com seus dados ou os da sua escola.',
                },
                {
                  step: '02',
                  icon: '/icons/icon5.png',
                  title: 'Análise',
                  text: 'Nossa equipe avalia a documentação e verifica os critérios técnicos e pedagógicos necessários.',
                },
                {
                  step: '03',
                  icon: '/icons/icon9.png',
                  title: 'Aprovação',
                  text: 'Receba seu reconhecimento institucional formal e passe a integrar oficialmente a Federação.',
                },
                {
                  step: '04',
                  icon: '/icons/icon8.png',
                  title: 'Certificação',
                  text: 'Emita diplomas e certificados com validade nacional e internacional para seus alunos.',
                },
                {
                  step: '05',
                  icon: '/icons/icon6.png',
                  title: 'Crescimento',
                  text: 'Acesse suporte contínuo, atualizações profissionais e uma rede global de +100 países.',
                },
              ].map((s, i) => (
                <AnimatedSection key={i} animation="fade-up" delay={i * 120}>
                  <div className="relative flex flex-col items-center text-center group">
                    {/* number circle */}
                    <div className="relative z-10 w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-5 shadow-xl group-hover:scale-110 transition-transform duration-300">
                      <span className="text-2xl font-black text-white">{s.step}</span>
                    </div>
                    {/* icon */}
                    <div className="w-14 h-14 mb-4 relative">
                      <div className="absolute inset-0 bg-amber-400/20 rounded-xl blur-lg" />
                      <img src={s.icon} alt={s.title} className="relative z-10 w-14 h-14 object-contain drop-shadow-lg" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
                    <p className="text-amber-100 text-sm leading-relaxed max-w-[180px] mx-auto">{s.text}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>

          <AnimatedSection animation="fade-up" delay={600}>
            <div className="text-center mt-14">
              <Link
                to="/filiacao"
                className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-amber-400 to-orange-400 text-amber-900 font-black rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg"
              >
                <span>Quero me Filiar</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </AnimatedSection>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          4. BENEFÍCIOS EXCLUSIVOS — sem efeito de rotação ao clicar
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-amber-50 via-white to-amber-50/30">
        <div className="container mx-auto px-6 md:px-12">

          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                  <img src="/icons/icon3.png" alt="Vantagens Exclusivas" className="w-6 h-6 object-contain" />
                </div>
                <span className="text-sm font-bold text-amber-900 uppercase tracking-wider">Vantagens Exclusivas</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">Benefícios Exclusivos</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A filiação à Federação oferece uma gama completa de vantagens que fortalecem sua carreira e sua instituição
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[
              {
                icon: '/icons/icone2.png',
                title: 'Reconhecimento Institucional',
                description: 'Vinculação formal a uma entidade federativa de caráter educacional e institucional, conferindo credibilidade, legitimidade e posicionamento profissional no setor da beleza.',
              },
              {
                icon: '/icons/icon3.png',
                title: 'Validação Educacional e Profissional',
                description: 'Reconhecimento e validação de formações, cursos e trajetórias profissionais, alinhados a critérios técnicos, pedagógicos e normativos, com validade nacional e internacional.',
              },
              {
                icon: '/icons/icon8.png',
                title: 'Diplomação com Alcance Nacional e Internacional',
                description: 'Emissão de diplomas, certificados e documentos institucionais, com reconhecimento no Brasil e no exterior, favorecendo a mobilidade profissional e educacional.',
              },
              {
                icon: '/icons/icon9.png',
                title: 'Padrões Educacionais e Boas Práticas',
                description: 'Acesso a diretrizes, referenciais pedagógicos e boas práticas, promovendo a elevação do nível técnico, ético e educacional do setor.',
              },
              {
                icon: '/icons/icon4.png',
                title: 'Fortalecimento da Atuação Profissional',
                description: 'Posicionamento do profissional, escola ou instituição como integrante de um sistema educacional organizado, reconhecido e alinhado às exigências do mercado contemporâneo.',
              },
              {
                icon: '/icons/icon.png',
                title: 'Integração com uma Rede Institucional',
                description: 'Conexão com uma rede nacional e internacional de profissionais, educadores, escolas e instituições, promovendo intercâmbio, atualização e crescimento estruturado.',
              },
              {
                icon: '/icons/icon5.png',
                title: 'Suporte Técnico e Institucional Contínuo',
                description: 'Acompanhamento e orientação institucional em processos de filiação, reconhecimento, certificação e desenvolvimento educacional.',
              },
              {
                icon: '/icons/icon7.png',
                title: 'Autoridade e Credibilidade de Marca',
                description: 'Uso institucional da marca da Federação, fortalecendo a imagem, reputação e confiança perante alunos, parceiros e o mercado.',
              },
              {
                icon: '/icons/icon6.png',
                title: 'Alinhamento a Referenciais Internacionais',
                description: 'Atuação em consonância com parâmetros e práticas internacionais, reforçando a qualidade da formação e o reconhecimento além das fronteiras nacionais.',
              },
              {
                icon: '/icons/icon1.png',
                title: 'Convênios com Marcas de Cosméticos e Feiras da Beleza',
                description: 'Parcerias institucionais com marcas de cosméticos e feiras da beleza, promovendo acesso a conhecimento, inovação, capacitação e integração com o mercado profissional.',
              },
            ].map((benefit, idx) => (
              <AnimatedSection key={idx} animation="fade-up" delay={idx * 80}>
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-amber-100 h-full group">
                  <div className="relative w-16 h-16 mb-6 flex items-center justify-center">
                    <div className="absolute inset-0 bg-amber-400/20 rounded-xl blur-xl group-hover:bg-amber-400/35 transition-all duration-300" />
                    <img
                      src={benefit.icon}
                      alt={benefit.title}
                      className="relative z-10 w-12 h-12 object-contain drop-shadow-lg group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          5. QUANDO O TALENTO ENCONTRA PREPARO — inalterado
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <Quote className="w-16 h-16 text-amber-400 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-8 leading-tight">
                Quando o talento encontra preparo<br />e reconhecimento, a profissão ganha o mundo.
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-400 mx-auto" />
            </div>
          </AnimatedSection>
          <AnimatedSection animation="fade-up" delay={200}>
            <p className="text-lg md:text-xl text-amber-50 max-w-4xl mx-auto text-center leading-relaxed">
              A Federação não apenas valida competências, mas constrói carreiras sólidas, conectando profissionais qualificados a oportunidades globais e garantindo que sua formação seja reconhecida em qualquer lugar do mundo.
            </p>
          </AnimatedSection>
        </div>
        <AnimatedSection animation="fade-up" delay={600}>
            <div className="text-center mt-14">
              <Link
                to="/filiacao"
                className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-amber-400 to-orange-400 text-amber-900 font-black rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg"
              >
                <span>Quero me Filiar</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </AnimatedSection>
      </section>
       
      {/* ══════════════════════════════════════════════════════════════
          6. RECONHECIMENTO NACIONAL E ATUAÇÃO INTERNACIONAL — inalterado
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-300 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-orange-300 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">

          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                  <img src="/icons/icone2.png" alt="Certificação Oficial" className="w-6 h-6 object-contain" />
                </div>
                <span className="text-sm font-bold text-amber-900 uppercase tracking-wider">Certificação Oficial</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">
                Reconhecimento Nacional<br />e Atuação Internacional
              </h2>
            </div>
          </AnimatedSection>

          <div className="max-w-5xl mx-auto">
            <AnimatedSection animation="fade-up" delay={200}>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-10 md:p-16 shadow-xl border border-amber-200">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="relative w-20 h-20 mb-8">
                      <div className="absolute inset-0 bg-amber-400/20 rounded-2xl blur-xl" />
                      <img src="/icons/icon8.png" alt="Diploma Global" className="relative z-10 w-20 h-20 object-contain drop-shadow-lg" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Diploma com Validade Global</h3>
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      A Federação constitui-se como entidade institucional de caráter educacional, com reconhecimento em âmbito nacional e atuação internacional, exercendo papel relevante no reconhecimento, validação e qualificação das formações profissionais nos setores da beleza, estética e terapias complementares.
                    </p>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Sua atuação contribui para o alinhamento técnico e pedagógico do setor, fortalecendo sua inserção institucional junto a organismos públicos, privados e educacionais.
                    </p>
                  </div>
                  <div className="space-y-6">
                    {[
                      { icon: '/icons/icon4.png', title: 'Reconhecimento Nacional', description: 'Diplomas e certificados com validade em todo território brasileiro' },
                      { icon: '/icons/icon6.png', title: 'Atuação Internacional', description: 'Formação reconhecida em mais de 100 países ao redor do mundo' },
                      { icon: '/icons/icon5.png', title: 'Credibilidade Institucional', description: '70+ anos de tradição e excelência educacional' },
                      { icon: '/icons/icone2.png', title: 'Padrões Internacionais', description: 'Alinhamento com as melhores práticas pedagógicas globais' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex gap-4 items-start bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                        <div className="relative w-12 h-12 flex-shrink-0">
                          <div className="absolute inset-0 bg-amber-400/15 rounded-lg blur-lg" />
                          <img src={item.icon} alt={item.title} className="relative z-10 w-12 h-12 object-contain drop-shadow-md" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          7. ÁLBUM DE FORMANDOS — efeito livro com 8 fotos
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-amber-50 via-orange-50/30 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <AnimatedSection animation="fade-up">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">

                {/* LEFT — Book gallery */}
                <div className="flex items-center justify-center">
                  <BookGallery />
                </div>

                {/* RIGHT — Content */}
                <div>
                  <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                      <img src="/icons/icon9.png" alt="Conquista Profissional" className="w-6 h-6 object-contain" />
                    </div>
                    <span className="text-sm font-bold text-amber-900 uppercase tracking-wider">Conquista Profissional</span>
                  </div>

                  <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
                    Seu Diploma é Sua<br />Porta para o Mundo
                  </h2>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    Cada certificado emitido pela Federação representa não apenas a conclusão de um curso, mas o ingresso em uma rede global de profissionais qualificados e reconhecidos.
                  </p>
                  <p className="text-base text-gray-600 mb-8 leading-relaxed">
                    Com formação validada nacional e internacionalmente, nossos alunos conquistam oportunidades em qualquer lugar do mundo, levando consigo a credibilidade e o respaldo de mais de 70 anos de excelência educacional.
                  </p>
                  <div className="space-y-4 mb-10">
                    {[
                      'Certificação reconhecida globalmente',
                      'Suporte contínuo após formação',
                      'Rede de contatos internacional',
                      'Atualização profissional constante',
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-gray-700 font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/filiacao"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
                  >
                    <span>Comece Sua Jornada</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>

              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          8. CENTRAL DO FILIADO E ALUNO — inalterado
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-gray-900 via-amber-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">

          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/30 mb-6 shadow-lg">
                <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                  <img src="/icons/icon11.png" alt="Área Exclusiva" className="w-6 h-6 object-contain" />
                </div>
                <span className="text-sm font-bold text-amber-100 uppercase tracking-wider">Área Exclusiva</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6">Central do Filiado e Aluno</h2>
              <p className="text-lg text-amber-100 max-w-3xl mx-auto">
                Plataformas dedicadas para gerenciar sua filiação, cursos, certificados e muito mais
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">

            <AnimatedSection animation="slide-right" delay={200}>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="relative w-16 h-16 mb-6">
                  <div className="absolute inset-0 bg-amber-400/30 rounded-xl blur-xl" />
                  <img src="/icons/icon7.png" alt="Portal do Filiado" className="relative z-10 w-16 h-16 object-contain drop-shadow-lg" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Portal do Filiado</h3>
                <p className="text-amber-50 mb-8 leading-relaxed">
                  Profissionais e escolas filiadas gerenciam sua operação, alunos, documentos e acesso a recursos exclusivos da Federação.
                </p>
                <div className="space-y-3 mb-8">
                  {['Gestão de Alunos','Emissão de Certificados','Documentação Completa','Renovação Anual','Suporte Jurídico','Atualizações Regulatórias'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-amber-300 flex-shrink-0" />
                      <span className="text-amber-50">{item}</span>
                    </div>
                  ))}
                </div>
                <Link to="/login/Filiado" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-300 to-yellow-300 text-amber-900 font-bold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                  <span>Acessar Portal</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slide-left" delay={300}>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="relative w-16 h-16 mb-6">
                  <div className="absolute inset-0 bg-blue-400/30 rounded-xl blur-xl" />
                  <img src="/icons/icon.png" alt="Portal do Aluno" className="relative z-10 w-16 h-16 object-contain drop-shadow-lg" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Portal do Aluno</h3>
                <p className="text-amber-50 mb-8 leading-relaxed">
                  Alunos acessam seus cursos, materiais didáticos, acompanham progresso e obtém certificados digitais.
                </p>
                <div className="space-y-3 mb-8">
                  {['Área de Estudos','Materiais do Curso','Certificados Digitais','Histórico Acadêmico','Comunidade de Alunos','Suporte Pedagógico'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-300 flex-shrink-0" />
                      <span className="text-amber-50">{item}</span>
                    </div>
                  ))}
                </div>
                <Link to="/login/aluno" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-400 to-purple-400 text-white font-bold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                  <span>Acessar Portal</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </AnimatedSection>

          </div>

         

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          9. GALERIA E INSTAGRAM — inalterado
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12">

          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-pink-100 to-purple-100 backdrop-blur-sm rounded-full border border-pink-300/60 mb-6 shadow-sm">
                <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                  <Instagram className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm font-bold text-gray-900 uppercase tracking-wider">Comunidade</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">Acompanhe Nossa Jornada</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Siga nosso Instagram e veja de perto os bastidores, eventos, conquistas e histórias de sucesso dos nossos profissionais
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="fade-up" delay={200}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto mb-12">
              {[1,2,3,4,5,6,7,8].map((item) => (
                <div key={item} className="aspect-square bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl hover:scale-105 transition-transform duration-300 cursor-pointer shadow-lg overflow-hidden group relative">
                  <img src={`/is${item}.jpeg`} alt={`Instagram foto ${item}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-500/0 to-purple-500/0 group-hover:from-pink-500/90 group-hover:to-purple-500/90 transition-all duration-300 flex items-center justify-center">
                    <Instagram className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:scale-110" />
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection animation="fade-up" delay={300}>
            <div className="text-center">
              <a
                href="https://www.instagram.com/fepcssp?igsh=Zno2dWZhdnc1Mjl6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
              >
                <Instagram className="w-6 h-6" />
                <span>Seguir no Instagram</span>
              </a>
            </div>
          </AnimatedSection>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          10. ESCOLAS ASSOCIADAS — substitui e-commerce
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-amber-50 via-white to-orange-50/30">
        <div className="container mx-auto px-6 md:px-12">

          <AnimatedSection animation="fade-up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center">
                  <School className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-bold text-amber-900 uppercase tracking-wider">Rede Credenciada</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">Escolas Associadas</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Conheça as escolas e academias credenciadas pela Federação. Instituições com formação reconhecida, suporte institucional e padrões educacionais de excelência.
              </p>
            </div>
          </AnimatedSection>

          {/* Showcase cards — placeholder grid (data will come from /escolas) */}
         <EscolasGrid />
          {/* CTA */}
          <AnimatedSection animation="fade-up" delay={500}>
            <div className="text-center">
              <Link
                to="/escolas"
                className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-black rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg"
              >
                <School className="w-6 h-6" />
                <span>Ver Todas as Escolas Associadas</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="mt-4 text-sm text-gray-500">
                Sua escola ainda não está aqui?{' '}
                <Link to="/filiacao" className="text-amber-600 font-semibold hover:underline">
                  Saiba como se associar →
                </Link>
              </p>
            </div>
          </AnimatedSection>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          FOOTER — inalterado
      ══════════════════════════════════════════════════════════════ */}
      <footer className="bg-gradient-to-br from-amber-50 via-amber-100/40 to-white text-gray-800 pt-16 pb-8">
        <div className="container mx-auto px-6 md:px-12">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">

            <div>
              <h3 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-amber-600" />
                Sobre a Federação
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Entidade que representa, certifica e eleva o padrão dos profissionais da beleza, estética e terapias complementares em mais de 100 países, com mais de 70 anos de história.
              </p>
              
            </div>

            <div>
              <h3 className="text-xl font-black text-gray-900 mb-4">Links Rápidos</h3>
              <ul className="space-y-2">
                {[
                  { label: 'Filiação', to: '/filiacao' },
                  
                  { label: 'Cursos', to: '/cursos' },
                  { label: 'Eventos', to: '/noticias' },
                  
                ].map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.to} className="text-gray-700 hover:text-amber-600 transition-colors flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-black text-gray-900 mb-4">Institucional</h3>
              <ul className="space-y-2">
                {[
                  { label: 'Nossa História', to: 'institucional/historia' },
                  { label: 'Diretoria', to: 'institucional/diretoria' },
                  { label: 'Atribuições', to: 'institucional/atribuicoes' },
                  { label: 'LGPD', to: 'institucional/lgpd' },
           
                ].map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.to} className="text-gray-700 hover:text-amber-600 transition-colors flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-black text-gray-900 mb-4">Contato</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Rua Paulo de Faria, 182 - Sala 46 - Cep: 02.267-000 - Tucuruvi - São Paulo</span>
                </li>
                <li className="flex items-start gap-2">
                  <Globe className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Atuação em +100 países</span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Desde 1953</span>
                </li>
              </ul>
            </div>

          </div>

          <div className="border-t border-amber-200 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-600 text-center md:text-left">
                © 1953–2025 | Federação das Escolas Profissionais de Cabeleireiros e Similares do Brasil - Internacional. Todos os direitos reservados.
              </p>

              {/* Selo OSC - IPEA */}
              <a
                href="https://mapaosc.ipea.gov.br/selo-osc/1492055"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
              >
                <img
                  src="https://mapaosc.ipea.gov.br/img/logo.png"
                  alt="Selo OSC - Mapa das Organizações da Sociedade Civil"
                  className="h-8 w-auto"
                />
              </a>

              <div className="flex gap-6 text-sm">
                <Link to="/privacidade" className="text-gray-600 hover:text-amber-600 transition-colors">Política de Privacidade</Link>
                <Link to="/termos" className="text-gray-600 hover:text-amber-600 transition-colors">Termos de Uso</Link>
              </div>
            </div>
          </div>

        </div>
      </footer>

    </div>
  );
};

export default Home;
